import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sendPasswordResetEmail } from '@/lib/auth';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import DocumentHead from '@/components/DocumentHead';
import { Turnstile } from '@/components/Turnstile';
import { Honeypot } from '@/components/Honeypot';
import { useSettings } from '@/hooks/useProducts';

export default function ForgotPassword() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  // See Login.tsx — same DB-first, env-fallback resolution.
  const { data: settingsData } = useSettings();
  const turnstileSiteKey =
    (settingsData?.settings?.turnstile_site_key as string | undefined) ||
    import.meta.env.VITE_TURNSTILE_SITE_KEY ||
    '';

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (!email) {
      setError(t('forgotPassword.errors.missingEmail'));
      return;
    }

    setIsSubmitting(true);
    try {
      await sendPasswordResetEmail(email, turnstileToken || undefined);
      setIsSubmitted(true);
    } catch (err: any) {
      setError(err?.message || t('forgotPassword.errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="flex flex-1 items-center justify-center px-4 py-12">
      <DocumentHead title={t('forgotPassword.title')} noIndex />
      <div className="w-full max-w-md">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-ink">{t('forgotPassword.heading')}</h1>
          <p className="mt-2 text-ink-soft">
            {t('forgotPassword.subtitle')}
          </p>
        </div>

        <div className="mt-8 rounded-xl border border-line bg-surface p-6 shadow-sm sm:p-8">
          {isSubmitted ? (
            <div className="text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sage-100">
                <svg className="h-6 w-6 text-sage-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
              <h2 className="mt-4 text-lg font-semibold text-ink">{t('forgotPassword.successHeading')}</h2>
              <p className="mt-2 text-sm text-ink-soft">
                {t('forgotPassword.successDescription')}
              </p>
              <Link
                to="/login"
                className="mt-6 inline-block text-sm font-medium text-sage-700 hover:text-sage-800"
              >
                {t('forgotPassword.backToLogin')}
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div
                  className="mb-6 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                  role="alert"
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate className="space-y-4 relative">
                <Honeypot />
                <Input
                  label={t('forgotPassword.emailLabel')}
                  type="email"
                  autoComplete="email"
                  required
                  placeholder={t('forgotPassword.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />

                <Turnstile
                  siteKey={turnstileSiteKey}
                  onVerify={setTurnstileToken}
                />

                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={!!turnstileSiteKey && !turnstileToken}
                  className="w-full"
                  size="lg"
                >
                  {t('forgotPassword.submit')}
                </Button>
              </form>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-ink-soft">
          {t('forgotPassword.rememberPrompt')}{' '}
          <Link
            to="/login"
            className="font-medium text-sage-700 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
          >
            {t('forgotPassword.signInLink')}
          </Link>
        </p>
      </div>
    </main>
  );
}
